import { BellowFieldDescription, DatePicker, Field, useFormValue } from '@startlibs/form'
import {SplitColumnsContainer} from '@startlibs/components';
import {getColor, media, smoothScroll} from '@startlibs/utils'
import React, {useEffect, useRef} from 'react'
import SignaturePad from 'signature_pad'
import styled from 'styled-components'
import { Card, SectionHeading } from '../../components/PageLayout'
import {ReleaseTerms} from './ReleaseTerms'
import { UPLOAD } from '../../enums/UploaderManagement';
import { getContact, getRelationLabel } from '../../enums/ContactRelationKind';
import {FormattedMessage, useIntl} from 'react-intl';
import {defaultDateFormat} from "../../utils/dateFormat";

const SignaturePadContainer = styled("canvas")`
  background: white;
  border: 1px solid ${getColor('gray210')};
  border-radius: 5px;
  display: block;
  margin-bottom: .5rem;
  width: 100%;
`
const DocumentDateInputsContainer = styled.div`
  margin-bottom: 2rem;
  ${media.desktop`
    display: flex;
    > * {
    flex-grow: 1;
      :first-child {
        flex-basis: 60%;
        margin-right: 3rem;
      }
    }
  `}
 ${BellowFieldDescription} {
   margin-top: -0.5rem;
   font-size: 11px;
 }
`

const AlignDatepickerRight = styled.div`
  div[tabindex] > div {
    right: 0;
  }
`

export const ReleaseSignoff = ({caseRequest, signaturePad, focusLocations, mode = '', htmlTerm, setHtmlTerm, setTermLanguage}) => {

  const intl = useIntl()
  const signatureRef = useRef()
  const dateFrom = useRef()
  const dateTo = useRef()
  const dateExpiration = useRef()
  const header = useRef()

  const contact = getContact(intl, caseRequest)
  const contactRelationType = getRelationLabel(intl,caseRequest.contactDetails.contactRelationType,'representative')

  const [records] = useFormValue('records')

  useEffect(() => {
    const canvas = signatureRef.current
    canvas.width = canvas.offsetWidth;
    signaturePad.openWith(new SignaturePad(canvas, {backgroundColor: "rgb(255,255,255)"}));
    const resizeEvent = () => {
      const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      canvas.width = canvas.offsetWidth;
      canvas.getContext('2d').putImageData(imageData, 0, 0)
    }
    window.addEventListener("resize", resizeEvent);
    return () => window.removeEventListener("resize", resizeEvent);
  }, [])

  const focus = (field) => () => {
    if (field === "locations") {
      focusLocations()
    } else {
      smoothScroll({target:header.current,offset:-80})
      if (field === 'dateFrom') {
        dateFrom.current?.focus()
      } else if (field === 'dateTo') {
        dateTo.current?.focus()
      } else if (field === 'dateExpiration') {
        dateExpiration.current?.focus()
      }
    }
  }

  return <>
    <SectionHeading ref={header}>
      <h4><FormattedMessage
        defaultMessage="Medical records release"
        description="Release Signoff title"
      /></h4>
    </SectionHeading>
    <Card css={mode === UPLOAD ? "padding: 0; border: 0px;" : "" }>
      <DocumentDateInputsContainer>
        <Field
          label={intl.formatMessage({
            defaultMessage: "Authorize access to medical records registered between",
            description: "Medical records release form, date range input label"
          })}
          bellowDescText={intl.formatMessage({
            defaultMessage: "Only records registered between these dates will be authorized",
            description: "Medical records release form, date range input description"
          })}
          mandatory
        >
          <SplitColumnsContainer>
            <DatePicker
              ref={dateFrom}
              format={defaultDateFormat(intl)}
              path="dateFrom"
              placeholder={defaultDateFormat(intl).toUpperCase()}
            />
            <AlignDatepickerRight>
            <DatePicker
              format={defaultDateFormat(intl)}
              path="dateTo"
              ref={dateTo}
              placeholder={defaultDateFormat(intl).toUpperCase()}
            />
            </AlignDatepickerRight>
          </SplitColumnsContainer>
        </Field>
        <Field label={intl.formatMessage({
          defaultMessage: "Expiration date",
          description: "Medical records release form, expiration date input label"
        })}
          bellowDescText={intl.formatMessage({
            defaultMessage: "No records will be authorized after this date",
            description: "Medical records release form, expiration date input description"
          })} mandatory>
          <DatePicker
            format={defaultDateFormat(intl)}
            path="dateExpiration"
            ref={dateExpiration}
            placeholder={defaultDateFormat(intl).toUpperCase()}
          />
        </Field>
      </DocumentDateInputsContainer>
      <h4><FormattedMessage
        defaultMessage="Authorization for Use and Disclosure of Protected Health Information"
        description="Release Signoff authorization title"
      /></h4>
      {/* <p>For purposes of this Agreement, “We” or “Us” refers to Medical Institution from which you accessed this system and “You” refers to the Patient identified.</p> */}
      <ReleaseTerms focus={focus} caseRequest={caseRequest} htmlTerm={htmlTerm} setHtmlTerm={setHtmlTerm} isNew={true} setTermLanguage={setTermLanguage}/>
      <p css="margin-bottom:2rem">
        <b><FormattedMessage
          defaultMessage="By signing below you confirm that you have have read and agree to the above medical records release authorization."
          description="Release Signoff agreement text"
        /></b>
      </p>
      <Field label={intl.formatMessage({
        defaultMessage: "Digital signature",
        description: "Release Signoff digital signature input label"
      })}
        descText={intl.formatMessage({
          defaultMessage: "Please sign below to agree to the terms.",
          description: "Release Signoff digital signature input description"
        })} mandatory>
        <SignaturePadContainer height={120} ref={signatureRef} css="height: 120px; max-height: 120px;"/>
      </Field>
      <div>
        <p><b><FormattedMessage
          defaultMessage="Signed by"
          description="Medical release document signed by label"
          />: </b><b className='fs-exclude'>{contact.name}</b></p>
        <p><b><FormattedMessage
          defaultMessage="Relationship to Patient"
          description="Medical release document signed by relationship label"
          />: {contactRelationType == 'Client' ? intl.formatMessage({
          defaultMessage: 'Patient',
          description: 'Medical release document signed by relationship value'
        }): contactRelationType}</b></p>
      </div>
    </Card>
    
    {/*<AcceptTermsCheckbox caseRequest={caseRequest}/>*/}
  </>
}
